import React from 'react';
import ReportCard from '../components/ReportCard';
import StackedShareBar from '../components/StackedShareBar';
import RuleList from '../components/RuleList';
import SegmentLegend from '../components/SegmentLegend';
import { Mono } from '../components/Emphasis';
import { BUCKET_META, INFLECTION_CONTEXT, MIN_COHORT, SMALL_SAMPLE_NOTE } from '../copy';
import { fmtCount, fmtPct, share, shareAtLeast, tip, worstBandLabel } from '../format';

const AgeRow = ({ band, highlighted }) => {
  const total = band.total;
  const tooSmall = total > 0 && total < MIN_COHORT;
  const twoPlus = shareAtLeast(band.buckets, 2);

  const segments = band.buckets.map((bucket, index) => ({
    key: bucket.flags,
    length: share(bucket.count, total),
    tip: tip(
      `Age ${band.band} · ${BUCKET_META[index].label}`,
      `${fmtPct(share(bucket.count, total))} — ${fmtCount(bucket.count)} of ${fmtCount(total)}`
    ),
  }));

  return (
    <div
      className={`grid items-center gap-ds-4 ${
        highlighted ? '-mx-ds-2 rounded-[8px] bg-ds-amber-lt px-ds-2 py-ds-2' : ''
      }`}
      style={{ gridTemplateColumns: '56px 1fr 96px' }}
    >
      <div
        className={`font-jetbrains-mono text-[12px] ${
          highlighted ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'
        }`}
      >
        {band.band}
      </div>
      {total ? <StackedShareBar segments={segments} /> : <div className='h-[26px] rounded-ds-sm bg-gray-200' />}
      <div
        className={`text-right font-jetbrains-mono text-[12px] ${
          highlighted ? 'font-bold' : 'font-semibold'
        } text-gray-900`}
        title={tooSmall ? SMALL_SAMPLE_NOTE : undefined}
      >
        {total && !tooSmall ? `${fmtPct(twoPlus)} at 2+` : '—'}
      </div>
    </div>
  );
};

/**
 * Abnormality burden by age band. The band carrying the highest share at the top bucket is highlighted
 * and named as the inflection point — derived from the data, so it moves with the company.
 */
const MetabolicByAge = ({ data }) => {
  const worst = worstBandLabel(data.by_age);
  const populated = data.by_age.filter((band) => band.total);
  const first = populated[0];
  const worstBand = data.by_age.find((band) => band.band === worst);
  const climb = first && worstBand
    ? { from: shareAtLeast(first.buckets, 2), to: shareAtLeast(worstBand.buckets, 2) }
    : null;
  // Only claim burden rises with age when the oldest-affected band is genuinely worse than the youngest.
  const climbs = Boolean(climb) && climb.to > climb.from;

  return (
    <div className='mt-ds-9 grid grid-cols-1 items-start gap-ds-5 lg:grid-cols-[1.5fr_1fr]'>
      <ReportCard fileName='risk-by-age-band' className='p-[26px]'>
        <h3 className='m-0 font-fig-extrabold text-[26px] font-bold leading-[1.18] text-gray-900'>
          {climbs ? 'Risk burden climbs steadily with age' : 'Risk burden does not track age in this cohort'}
        </h3>
        <p className='mb-ds-6 mt-[6px] text-xs text-gray-600'>Share of each age band by abnormality count</p>
        <SegmentLegend labels={['None', 'One', 'Two', 'Three or more']} />
        <div className='grid gap-ds-3'>
          {data.by_age.map((band) => (
            <AgeRow key={band.band} band={band} highlighted={band.band === worst} />
          ))}
        </div>
      </ReportCard>

      <div className='rounded-ds-lg border border-gray-300 bg-white p-[26px]'>
        <div className='mb-ds-4 text-ts-xs font-semibold uppercase tracking-[0.12em] text-ds-amber-dk'>
          {worst ? `${worst} is the inflection point` : 'Age pattern'}
        </div>
        <RuleList
          items={[
            climb && first.band !== worst ? (
              <>
                2+ abnormalities: <Mono>{fmtPct(climb.from)}</Mono> (age {first.band}) →{' '}
                <Mono>{fmtPct(climb.to)}</Mono> (age {worst})
                {climb.from > 0 ? `, roughly ${(climb.to / climb.from).toFixed(1)}x.` : '.'}
              </>
            ) : null,
            worstBand ? (
              <>
                <Mono>{fmtPct(shareAtLeast(worstBand.buckets, 3))}</Mono> of {worst} year-olds already meet the 3+
                syndrome threshold — the highest of any band.
              </>
            ) : null,
            INFLECTION_CONTEXT,
          ]}
        />
      </div>
    </div>
  );
};

export default MetabolicByAge;
